let _audioContext = null;
let _analyser = null;
let _sourceNode = null;
let _timeDataArray = null;
let _freqDataArray = null;
let _monitorInterval = null;

// Rolling buffer of recent mouth-aperture samples, used to tell "lips are
// moving" apart from "mouth is just open" (e.g. yawning).
let _mouthSamples = [];
let _lipMovingUntil = 0;

const SAMPLE_INTERVAL_MS = 700;
const SUSTAINED_DURATION_MS = 1000;

// Normalized RMS amplitude (0-1) from the mic track. Ambient/background
// noise crosses the lower threshold; a student actually talking pushes
// well past it.
const BACKGROUND_NOISE_THRESHOLD = 0.03;
const SPEAKING_THRESHOLD = 0.04;

// Human speech concentrates most of its energy in this band -- used to
// filter out non-vocal noise (AC hum, keyboard clatter) so only actual
// human sound gets logged/flagged.
const VOICE_BAND_LOW_HZ = 85;
const VOICE_BAND_HIGH_HZ = 3000;
const VOICE_BAND_ENERGY_RATIO = 0.45;
const MIN_TOTAL_ENERGY = 40;

// How much the mouth-aperture ratio must swing within the tracking window
// to count as "moving" rather than static.
const MOUTH_MOVEMENT_WINDOW_MS = 900;
const MOUTH_RANGE_THRESHOLD = 0.012;
const LIP_ACTIVITY_HOLD_MS = 2000;

function getRmsVolume(analyser, dataArray) {
  analyser.getByteTimeDomainData(dataArray);
  let sumSquares = 0;
  for (let i = 0; i < dataArray.length; i++) {
    const normalized = (dataArray[i] - 128) / 128;
    sumSquares += normalized * normalized;
  }
  return Math.sqrt(sumSquares / dataArray.length);
}

function isHumanVoiceBand(analyser, dataArray, sampleRate) {
  analyser.getByteFrequencyData(dataArray);

  const nyquist = sampleRate / 2;
  const binHz = nyquist / dataArray.length;
  const lowBin = Math.max(1, Math.round(VOICE_BAND_LOW_HZ / binHz));
  const highBin = Math.min(
    dataArray.length - 1,
    Math.round(VOICE_BAND_HIGH_HZ / binHz)
  );

  let bandEnergy = 0;
  let totalEnergy = 0;
  for (let i = 0; i < dataArray.length; i++) {
    totalEnergy += dataArray[i];
    if (i >= lowBin && i <= highBin) bandEnergy += dataArray[i];
  }

  if (totalEnergy < MIN_TOTAL_ENERGY) return false;
  return bandEnergy / totalEnergy >= VOICE_BAND_ENERGY_RATIO;
}

export const noiseState = {
  noiseStart: null,
  speakingStart: null,
};

export const noiseGetters = {};

export const noiseMutations = {
  SET_NOISE_START(state, val) {
    state.noiseStart = val;
  },
  SET_SPEAKING_START(state, val) {
    state.speakingStart = val;
  },
};

export const noiseActions = {
  // Fed landmarks on every FaceMesh tick (see cameraProctoring.js). Tracks
  // whether the mouth is actively opening/closing, as opposed to just
  // being open (yawning) or closed -- that's what "speaking" means here.
  trackLipMovement(_ctx, landmarks) {
    if (!landmarks) return;
    const forehead = landmarks[10];
    const chin = landmarks[152];
    const upperLip = landmarks[13];
    const lowerLip = landmarks[14];
    if (!forehead || !chin || !upperLip || !lowerLip) return;

    const faceHeight =
      Math.hypot(chin.x - forehead.x, chin.y - forehead.y) || 1;
    const mouthGap = Math.hypot(
      upperLip.x - lowerLip.x,
      upperLip.y - lowerLip.y
    );
    const ratio = mouthGap / faceHeight;

    const now = Date.now();
    _mouthSamples.push({ t: now, ratio });
    while (
      _mouthSamples.length &&
      now - _mouthSamples[0].t > MOUTH_MOVEMENT_WINDOW_MS
    ) {
      _mouthSamples.shift();
    }

    if (_mouthSamples.length >= 3) {
      const ratios = _mouthSamples.map((s) => s.ratio);
      const range = Math.max(...ratios) - Math.min(...ratios);

      if (range >= MOUTH_RANGE_THRESHOLD) {
        _lipMovingUntil = now + LIP_ACTIVITY_HOLD_MS;
      }
    }
  },

  async startNoiseDetection({ state, commit, dispatch, getters }) {
    const isScribe = localStorage.getItem("is_scribe") === "true";
    if (isScribe) return;
    if (_monitorInterval) return;

    const stream = getters.cameraStream;
    const audioTrack = stream && stream.getAudioTracks()[0];
    if (!audioTrack) return;

    try {
      const AudioContextClass =
        window.AudioContext || window.webkitAudioContext;
      _audioContext = new AudioContextClass();
      _sourceNode = _audioContext.createMediaStreamSource(
        new MediaStream([audioTrack])
      );
      _analyser = _audioContext.createAnalyser();
      _analyser.fftSize = 1024;
      _sourceNode.connect(_analyser);
      _timeDataArray = new Uint8Array(_analyser.frequencyBinCount);
      _freqDataArray = new Uint8Array(_analyser.frequencyBinCount);

      // Chrome/Safari create a new AudioContext in the "suspended" state
      // unless it's resumed after a user gesture -- without this, the
      // analyser only ever reads silence and volume never crosses a
      // threshold.
      if (_audioContext.state === "suspended") {
        await _audioContext.resume();
      }

      console.log(
        "[Noise Detection] Started -- AudioContext state:",
        _audioContext.state,
        "sampleRate:",
        _audioContext.sampleRate
      );
    } catch (err) {
      console.warn("[Proctoring] Noise detection init failed:", err);
      return;
    }

    _monitorInterval = setInterval(() => {
      if (!_analyser) return;

      const volume = getRmsVolume(_analyser, _timeDataArray);
      const now = Date.now();
      const lipsMoving = now < _lipMovingUntil;
      const isHumanSound =
        volume >= BACKGROUND_NOISE_THRESHOLD &&
        isHumanVoiceBand(_analyser, _freqDataArray, _audioContext.sampleRate);

      if (isHumanSound && lipsMoving) {
        // The student's lips are moving in step with detected human sound
        // -- this is the student talking, an actual violation.
        commit("SET_NOISE_START", null);
        if (!state.speakingStart) {
          commit("SET_SPEAKING_START", now);
        } else if (now - state.speakingStart > SUSTAINED_DURATION_MS) {
          console.warn("[Noise Detection] Speaking Detected violation fired", {
            volume: volume.toFixed(4),
            duration: now - state.speakingStart,
          });
          dispatch("triggerViolation", "Speaking Detected");
          commit("SET_SPEAKING_START", null);
        }
      } else if (isHumanSound && volume >= SPEAKING_THRESHOLD) {
        // Loud human-sounding noise without lip movement — could be someone
        // else talking nearby, TV, etc. Log it as background noise.
        commit("SET_SPEAKING_START", null);
        if (!state.noiseStart) {
          commit("SET_NOISE_START", now);
        } else if (now - state.noiseStart > SUSTAINED_DURATION_MS * 2) {
          console.warn("[Noise Detection] Background noise detected logged", {
            volume: volume.toFixed(4),
            duration: now - state.noiseStart,
          });
          dispatch("triggerViolation", "Background noise detected");
          commit("SET_NOISE_START", null);
        }
      } else {
        commit("SET_NOISE_START", null);
        commit("SET_SPEAKING_START", null);
      }
    }, SAMPLE_INTERVAL_MS);
  },

  stopNoiseDetection({ commit }) {
    console.log("[Noise Detection] Stopped");
    if (_monitorInterval) {
      clearInterval(_monitorInterval);
      _monitorInterval = null;
    }
    if (_sourceNode) {
      try {
        _sourceNode.disconnect();
      } catch (e) {
        // already disconnected
      }
      _sourceNode = null;
    }
    if (_audioContext) {
      _audioContext.close().catch(() => {});
      _audioContext = null;
    }
    _analyser = null;
    _timeDataArray = null;
    _freqDataArray = null;
    _mouthSamples = [];
    _lipMovingUntil = 0;
    commit("SET_NOISE_START", null);
    commit("SET_SPEAKING_START", null);
  },
};
